import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ClickableRow } from '@/components/list/ClickableRow';
import { configurationDate } from './configuration-date';
import type { Purpose } from './api';
import type { usePurposeQuery } from './use-purpose-query';
type View = ReturnType<typeof usePurposeQuery>;
function markReturn(event: MouseEvent) {
  document.querySelector('[data-purpose-return]')?.removeAttribute('data-purpose-return');
  (event.currentTarget as HTMLElement).setAttribute('data-purpose-return', '');
}
const arrows = { asc: ' ↑', desc: ' ↓' };
function sortState(view: View, column: string) {
  if (view.query.sort !== column) return 'none';
  return view.query.direction === 'asc' ? 'ascending' : 'descending';
}
function SortHeader({
  column,
  view,
}: {
  readonly column: { value: string; label: string };
  readonly view: View;
}) {
  return (
    <th key={column.value} className="p-3 font-semibold" aria-sort={sortState(view, column.value)}>
      <button className="min-h-11 text-left" onClick={() => view.order(column.value)}>
        {column.label}
        {view.query.sort === column.value ? arrows[view.query.direction ?? 'asc'] : ''}
      </button>
    </th>
  );
}
export function PurposeTable({ items, view }: { readonly items: Purpose[]; readonly view: View }) {
  const { t } = useTranslation('expenses');
  const sortOptions = (
    ['code', 'label', 'noteTypes', 'active', 'restricted', 'updatedAt'] as const
  ).map((value) => ({ value, label: t(`configuration.columns.${value}`) }));
  return (
    <div className="hidden overflow-x-auto rounded-xl border border-border lg:block">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted">
          <tr>
            {sortOptions.map((column) => (
              <SortHeader key={column.value} column={column} view={view} />
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <PurposeRow key={p.id} purpose={p} view={view} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
function PurposeRow({ purpose: p, view }: { readonly purpose: Purpose; readonly view: View }) {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  return (
    <ClickableRow onOpen={() => void navigate(view.target(p.id))}>
      <td className="p-3">
        <Link
          className="text-foreground font-semibold hover:underline inline-flex min-h-11 items-center"
          to={view.target(p.id)}
          onClick={markReturn}
        >
          {p.code}
        </Link>
      </td>
      <td className="max-w-64 p-3">
        <Link
          to={view.target(p.id)}
          className="inline-flex min-h-11 items-center"
          onClick={markReturn}
        >
          {p.label}
        </Link>
      </td>
      <td className="p-3">
        <TypeLinks purpose={p} />
      </td>
      <td className="p-3">{t(`configuration.${p.active ? 'active' : 'archived'}`)}</td>
      <td className="p-3">{t(`configuration.${p.restricted ? 'restricted' : 'open'}`)}</td>
      <td className="p-3 whitespace-nowrap">{configurationDate(p.updatedAt)}</td>
    </ClickableRow>
  );
}
export function PurposeCards({ items, view }: { readonly items: Purpose[]; readonly view: View }) {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  return (
    <div className="grid gap-3 lg:hidden">
      {items.map((p) => (
        <article
          key={p.id}
          className="bg-card space-y-2 rounded-xl border border-border p-4"
          onClick={(event) => {
            if (!(event.target as HTMLElement).closest('a')) void navigate(view.target(p.id));
          }}
        >
          <Link
            className="text-foreground inline-flex min-h-11 items-center gap-2 font-semibold hover:underline"
            to={view.target(p.id)}
            onClick={markReturn}
          >
            <span>{p.code}</span>
            <span>{p.label}</span>
          </Link>
          <p className="text-muted-foreground text-sm">
            {t(`configuration.${p.active ? 'active' : 'archived'}`)} ·{' '}
            {t(`configuration.${p.restricted ? 'restricted' : 'open'}`)}
          </p>
          <TypeLinks purpose={p} />
          <p className="text-muted-foreground text-xs">{configurationDate(p.updatedAt)}</p>
        </article>
      ))}
    </div>
  );
}
function TypeLinks({ purpose }: { readonly purpose: Purpose }) {
  const { t } = useTranslation('expenses');
  return (
    <div className="flex flex-wrap gap-x-3">
      {purpose.noteTypes.map((type) => (
        <Link
          key={type}
          to={`/expenses/settings/note-types?type=${type}`}
          className="text-foreground font-semibold hover:underline inline-flex min-h-11 items-center text-sm"
        >
          {t(`reports.noteType.${type}`)}
        </Link>
      ))}
    </div>
  );
}
